import * as utils from '../utils';
import { makeSDK } from '@applitools/eyes-sdk-core';
import { WebDriver } from './webdriver';
import { TimeoutError } from '../errors';
import { getLogger } from '../commons/logger';
import { CookieUtils } from './utils/cookieUtils';
import { TabService } from './services/tabService';
import { portSelectorMock } from './services/portSelector';
import { StepActionUtils } from './utils/stepActionUtils';
import { frameLocatorFactory } from './services/frameLocator';
import { isDebuggerConnected } from '../commons/detectDebugger';
import { getSessionPlayer } from '../commons/getSessionPlayerRequire';
import { stepActionRegistrar } from './stepActions/stepActionRegistrar';
import { PlaybackTimeoutCalculator } from './services/playbackTimeoutCalculator';
import * as config from '../commons/config';
import type { Step } from 'clickim/src/common/models/step';
import type { SessionPlayer } from 'clickim/src/background/session/sessionPlayer';
import type { StepActionFactory } from 'clickim/src/background/stepActions/stepActionFactory';

const logger = getLogger('SeleniumTestPlayer');

export class SeleniumTestPlayer {
    public driver: WebDriver;
    public readonly sessionPlayer: SessionPlayer;
    private readonly tabService: TabService;
    private readonly stepActionFactory: StepActionFactory;
    private readonly playbackTimeoutCalculator = new PlaybackTimeoutCalculator(isDebuggerConnected());

    constructor(
        public readonly id: number,
        userParamsData: object,
        shouldMonitorPerformance: boolean | undefined,
        driver: WebDriver | null | undefined,
        testRetryCount: number = undefined!,
        previousTestResultId: string | undefined = undefined,
    ) {
        const { SessionPlayer, commonConstants, StepActionFactory } = getSessionPlayer();
        this.driver = driver ?? new WebDriver();

        const stepActionUtils = new StepActionUtils(this.driver);
        this.stepActionFactory = new StepActionFactory(stepActionUtils);
        stepActionRegistrar(this.driver, this.stepActionFactory);

        this.tabService = this.driver.tabService || new TabService(this.driver);
        // expose the tabService on the driver so future sessions reusing the driver know the tabs (for example - a ******** step)
        this.driver.tabService = this.tabService;

        this.tabService.createSesion(id);

        const FrameLocator = frameLocatorFactory(this.driver);

        this.sessionPlayer = new SessionPlayer(
            id,
            {
                tabService: this.tabService,
                cookieUtils: new CookieUtils(this.driver) as any,
                FrameLocator,
                portSelector: portSelectorMock,
                LocateElementPlayer: null,
                stepActionUtils,
                stepActionFactory: this.stepActionFactory,
                playbackTimeoutCalculator: this.playbackTimeoutCalculator,
                makeSDK,
            },
        );

        this.sessionPlayer.setShouldMonitorPerformance(Boolean(shouldMonitorPerformance));

        this.tabService.setAddFrameHandlerCallBack(this.sessionPlayer.addPlaybackFrameHandler.bind(this.sessionPlayer));

        this.sessionPlayer.playbackManager.isRemoteSession = true;
        this.sessionPlayer.playbackManager.isLocalRun = false;
        this.sessionPlayer.playbackManager.testRetryCount = testRetryCount;
        this.sessionPlayer.playbackManager.previousTestResultId = previousTestResultId;

        this.sessionPlayer.playbackManager.userParamsData = userParamsData || {};

        this.onStepCompleted = this.onStepCompleted.bind(this);

        this.sessionPlayer.playbackManager.on(commonConstants.playback.RESULT, this.onStepCompleted);
    }

    private onStepCompleted(result: any, testId: string, resultId: string, step: Step) {
        if (step?.isTabOpener) {
            this.tabService.addNewPopup(this.id, step.id).catch(() => null);
        }
    }

    public async onDone() {
        const { commonConstants } = getSessionPlayer();
        const END_DRIVER_TIMEOUT = 1000 * 60 * 2;
        try {
            await utils.promiseTimeout(this.driver?.end(), END_DRIVER_TIMEOUT);
        } catch (error) {
            if (error instanceof TimeoutError) {
                await this.driver?.forceEnd().catch(() => null);
            }
        }
        this.sessionPlayer.playbackManager.off(commonConstants.playback.RESULT, this.onStepCompleted);
        Object.assign(this, { sessionPlayer: null, tabService: null, stepActionFactory: null, driver: null });
    }

    public clearSessionTabs() {
        this.tabService.clearAllTabs(this.id);
    }

    public async addTab(openerStepId?: string, options: { skipLoadInfo?: boolean; loadInfo?: boolean } = { loadInfo: true }) {
        const ids = await this.driver.getTabIds();
        if (!Array.isArray(ids)) {
            logger.error('addTab: driver.getTabIds() returned a non-array', { ids });
            return;
        }
        const lastId = ids.at(-1)!;
        await this.tabService.addNewTab(this.id, lastId, openerStepId, options);
        await this.sessionPlayer.addPlaybackFrameHandler(lastId as any as number, undefined, { emptyPage: true });
    }

    public async addAllTabs(openerStepId?: string | null, options = { loadInfo: true, checkForMainTab: true, takeScreenshots: true }, blockList: string[] = []) {
        const ids = await this.driver.getTabIds();
        // the ids are reversed so we search first in the last tab opened - otherwise it starts looking from the testim editor and not the AUT

        const PROHIBITED_URLS = [new URL(config.EDITOR_URL || config.DEFAULT_EDITOR_URL).hostname].concat(blockList);
        for (const id of ids.reverse()) {
            await this.tabService.addNewTab(this.id, id, openerStepId!, { ...options, forceSwitch: true });
            const tabInfo = this.tabService.getTabInfo(this.id, id);
            if (PROHIBITED_URLS.some(bad => tabInfo.url.includes(bad))) {
                this.tabService.removeTabInfo(this.id, id);
                continue;
            }
            await this.sessionPlayer.addPlaybackFrameHandler(id as any as number, undefined, { emptyPage: true });
        }
        if (this.tabService.tabCount(this.id) === 1) {
            // if we only have one tab because we removed the editor tab - we have to switchTab to one of the other tabs, otherwise tabService will assume it's on a good context but it's not.
            const tabInfo = this.tabService.getMainTabInfo(this.id);
            const tabUtils = this.tabService.getTabUtils(this.id, tabInfo);
            await this.tabService.switchTab(tabUtils.tabId, this.id, { forceSwitch: true });
        }
        // deal with checkForMainTab failing due to the page refreshing or JavaScript not responding or a similar issue
        this.tabService.fixMissingMainTab(this.id);
    }

    public getSessionId() {
        return this.driver.getSessionId();
    }
}
